import React, { useState } from 'react'
import { TextContent } from '../lessons/TextContent'
import type { Lesson } from '@/types/lesson'
import { lessonService as defaultLessonService } from '@/services/lessonService'

export interface ContentReviewModalProps {
  lesson: Lesson
  onClose: () => void
  onActionComplete: () => void
  service?: Pick<typeof defaultLessonService, 'approveLesson' | 'rejectLesson'>
}

const MIN_NOTE_LEN = 10

const CONTENT_TYPE_LABELS: Record<string, string> = {
  pdf: 'PDF',
  video: 'Video',
  text: 'Teks',
}

function getYouTubeEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url)
    let videoId: string | null = null
    if (u.hostname === 'youtu.be') {
      videoId = u.pathname.slice(1).split('?')[0]
    } else if (u.hostname.includes('youtube.com')) {
      videoId = u.searchParams.get('v')
    }
    if (!videoId) return null
    return `https://www.youtube.com/embed/${videoId}`
  } catch {
    return null
  }
}

function ContentPreview({ lesson }: { lesson: Lesson }) {
  if (lesson.contentType === 'pdf' && lesson.contentUrl) {
    return (
      <iframe
        src={lesson.contentUrl}
        title={lesson.title}
        className="w-full h-full min-h-[500px] rounded-xl border-0"
      />
    )
  }

  if (lesson.contentType === 'video' && lesson.contentUrl) {
    const youtubeEmbed = getYouTubeEmbedUrl(lesson.contentUrl)
    if (youtubeEmbed) {
      return (
        <iframe
          src={youtubeEmbed}
          title={lesson.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full aspect-video rounded-xl border-0"
        />
      )
    }
    return (
      <video
        src={lesson.contentUrl}
        controls
        className="w-full rounded-xl bg-black"
      />
    )
  }

  if (lesson.contentHtml) {
    return (
      <div data-testid="lesson-html-preview" className="p-4 bg-white dark:bg-white/5 rounded-xl border border-gray-200 dark:border-white/10 overflow-y-auto max-h-full">
        <TextContent content={lesson.contentHtml} />
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-gray-400">
      <span className="material-symbols-outlined text-5xl mb-3 opacity-30">description</span>
      <p className="text-sm">(Tidak ada konten yang bisa ditampilkan)</p>
    </div>
  )
}

/**
 * ContentReviewModal renders the full lesson preview for reviewers alongside
 * Approve / Reject actions. Reject requires a note of at least 10 characters.
 */
export const ContentReviewModal: React.FC<ContentReviewModalProps> = ({
  lesson,
  onClose,
  onActionComplete,
  service = defaultLessonService,
}) => {
  const [note, setNote] = useState('')
  const [mode, setMode] = useState<'preview' | 'reject'>('preview')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleApprove = async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await service.approveLesson(lesson.id)
      if (!res.success) {
        throw new Error(res.error?.message || 'Failed to approve')
      }
      onActionComplete()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve')
    } finally {
      setBusy(false)
    }
  }

  const handleReject = async () => {
    if (note.trim().length < MIN_NOTE_LEN) {
      setError(`Catatan harus minimal ${MIN_NOTE_LEN} karakter`)
      return
    }
    setBusy(true)
    setError(null)
    try {
      const res = await service.rejectLesson(lesson.id, note.trim())
      if (!res.success) {
        throw new Error(res.error?.message || 'Failed to reject')
      }
      onActionComplete()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject')
    } finally {
      setBusy(false)
    }
  }

  const contentTypeLabel = CONTENT_TYPE_LABELS[lesson.contentType] ?? lesson.contentType.toUpperCase()

  return (
    <div
      role="dialog"
      aria-label="Content review"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-white/10 rounded-3xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100 dark:border-white/10 shrink-0">
          <div className="flex items-center gap-4">
            <div className="size-10 rounded-2xl bg-primary/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-primary text-[20px]">
                {lesson.contentType === 'pdf' ? 'picture_as_pdf' : lesson.contentType === 'video' ? 'play_circle' : 'article'}
              </span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">{lesson.title}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                  {contentTypeLabel}
                </span>
                <span className="text-xs text-gray-400">·</span>
                <span className="text-xs text-gray-400">{lesson.durationMinutes} mnt</span>
                {lesson.submittedAt && (
                  <>
                    <span className="text-xs text-gray-400">·</span>
                    <span className="text-xs text-gray-400">
                      Dikirim {new Date(lesson.submittedAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="size-10 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Body: 2-column layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left: Content Preview */}
          <div className="flex-1 overflow-y-auto p-6 border-r border-gray-100 dark:border-white/10">
            <ContentPreview lesson={lesson} />
          </div>

          {/* Right: Info + Actions */}
          <div className="w-80 shrink-0 flex flex-col overflow-y-auto p-6 gap-6">
            {/* Description */}
            <div>
              <h3 className="text-xs font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-2">Deskripsi</h3>
              <p className="text-sm text-gray-700 dark:text-slate-300 leading-relaxed whitespace-pre-line">
                {lesson.description || <span className="text-gray-400 italic">Tidak ada deskripsi</span>}
              </p>
            </div>

            {/* Content URL for non-text types */}
            {(lesson.contentType === 'pdf' || lesson.contentType === 'video') && lesson.contentUrl && (
              <div>
                <h3 className="text-xs font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-2">URL Konten</h3>
                <a
                  href={lesson.contentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary hover:underline break-all leading-relaxed"
                >
                  {lesson.contentUrl}
                </a>
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-gray-100 dark:border-white/10" />

            {/* Error */}
            {error && (
              <div role="alert" className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex-1 flex flex-col justify-end gap-3">
              {mode === 'preview' ? (
                <>
                  <p className="text-xs text-gray-400 dark:text-slate-500 text-center">
                    Tinjau konten di sebelah kiri, lalu pilih tindakan:
                  </p>
                  <button
                    type="button"
                    onClick={handleApprove}
                    disabled={busy}
                    className="w-full px-4 py-3 rounded-2xl bg-green-500/15 text-green-500 dark:text-green-400 font-bold hover:bg-green-500/25 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                    data-testid="approve-button"
                  >
                    <span className="material-symbols-outlined text-[18px]">check_circle</span>
                    {busy ? 'Menyetujui…' : 'Setujui'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMode('reject'); setError(null) }}
                    disabled={busy}
                    className="w-full px-4 py-3 rounded-2xl bg-red-500/10 text-red-500 dark:text-red-400 font-bold hover:bg-red-500/20 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                    data-testid="open-reject-form"
                  >
                    <span className="material-symbols-outlined text-[18px]">cancel</span>
                    Tolak
                  </button>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 mb-2">
                      Alasan penolakan <span className="text-red-400">*</span>
                      <span className="font-normal text-gray-400"> (min. {MIN_NOTE_LEN} karakter)</span>
                    </label>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={5}
                      className="w-full rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 px-3 py-2.5 text-sm text-gray-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                      placeholder="Jelaskan apa yang perlu diperbaiki oleh instruktur…"
                      data-testid="reject-note"
                    />
                    <p className="text-[11px] text-gray-400 mt-1 text-right">{note.trim().length} / {MIN_NOTE_LEN} min</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleReject}
                    disabled={busy || note.trim().length < MIN_NOTE_LEN}
                    className="w-full px-4 py-3 rounded-2xl bg-red-500/20 text-red-400 font-bold hover:bg-red-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                    data-testid="submit-reject"
                  >
                    <span className="material-symbols-outlined text-[18px]">send</span>
                    {busy ? 'Mengirim…' : 'Kirim Penolakan'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMode('preview'); setError(null); setNote('') }}
                    disabled={busy}
                    className="w-full px-4 py-2 rounded-2xl bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-slate-400 text-sm hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                  >
                    Kembali
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContentReviewModal
